/**
 * Turn Illustration Generation Service
 * 
 * Generates consistent "Turn aesthetic" illustrations for scanned books.
 * Uses CENTRALIZED AI services (edge functions) for all API calls.
 * 
 * Architecture:
 * - Text completion via `completeText` (routes to ai-text-completion edge function)
 * - Vision analysis via `completeTextWithVision` (routes to ai-text-completion edge function)
 * - Image generation via `generateImage` (routes to ai-image-generation edge function)
 * - NO DIRECT API CALLS - all through edge functions
 * 
 * @see styles.ts for Turn aesthetic definitions
 */

import { generateImage, completeText, completeTextWithVision } from '@/lib/ai';
import { 
  buildTurnIllustrationPrompt, 
  inferSceneType,
  TurnSceneType,
  TURN_STYLE_BASE,
} from './styles';
import { logger } from '@/lib/logging';

// ============================================================================
// Types
// ============================================================================

export interface TurnIllustrationRequest {
  /** The text content of the page (used to understand context) */
  pageText: string;
  
  /** Page number for logging */
  pageNumber: number;
  
  /** Optional: Original scanned image (base64) for visual reference */
  originalImageBase64?: string;
  
  /** Optional: Scene description (if already analyzed) */
  sceneDescription?: string;
  
  /** Optional: Override the inferred scene type */
  sceneType?: TurnSceneType;
  
  /** Optional: Book title for context */
  bookTitle?: string;
  
  /** Optional: Book Story Bible for character consistency */
  storyBible?: BookStoryBible;
}

export interface TurnIllustrationResult {
  /** Base64-encoded image data URL */
  imageUrl: string;
  
  /** The prompt used for generation (for debugging) */
  prompt: string;
  
  /** Model used for generation */
  model: string;
  
  /** Time taken to generate (ms) */
  generationTimeMs: number;
}

/**
 * Book Story Bible - maintains consistency across pages.
 * Similar to Storybook Story Bible but simpler (extracted from scanned content).
 */
export interface BookStoryBible {
  /** Book title */
  title: string;
  
  /** Main characters with descriptions */
  characters: Array<{
    name: string;
    description: string;
  }>;
  
  /** Global setting/environment */
  setting: string;
  
  /** Overall mood */
  mood: string;
  
  /** Art style is ALWAYS Turn aesthetic (fixed, not configurable) */
  artStyle: typeof TURN_STYLE_BASE;
}

export interface SceneAnalysisResult {
  /** Description of what the illustration should show */
  sceneDescription: string;
  
  /** Inferred scene type */
  sceneType: TurnSceneType;
  
  /** Key subjects/characters in the scene */
  subjects: string[];
  
  /** Mood/atmosphere */
  mood: string;
}

// ============================================================================
// Book Story Bible
// ============================================================================

// In-memory cache for Story Bibles (cleared on page refresh)
const storyBibleCache = new Map<string, BookStoryBible>();

/**
 * Get or create a Story Bible for a book.
 * Analyzes first few pages to extract characters and setting.
 */
export async function getOrCreateStoryBible(
  bookId: string,
  bookTitle: string,
  pages: Array<{ pageNumber: number; text: string; illustrationDescription?: string }>
): Promise<BookStoryBible> {
  // Check cache first
  const cached = storyBibleCache.get(bookId);
  if (cached) {
    logger.debug(`[TurnIllustration] Using cached Story Bible for ${bookTitle}`);
    return cached;
  }
  
  logger.debug(`[TurnIllustration] Creating Story Bible for ${bookTitle}...`);
  
  // Combine first few pages for analysis
  const contextPages = pages.slice(0, 5);
  const combinedText = contextPages
    .map(p => p.text)
    .filter(t => t && t.trim())
    .join('\n\n');
  
  const combinedDescriptions = contextPages
    .map(p => p.illustrationDescription)
    .filter(d => d && d.trim())
    .join('\n');
  
  // Analyze to extract characters and setting
  const storyBible = await analyzeBookContent(bookTitle, combinedText, combinedDescriptions);
  
  // Cache it
  storyBibleCache.set(bookId, storyBible);
  
  logger.debug(`[TurnIllustration] Story Bible created: ${storyBible.characters.length} characters, setting: ${storyBible.setting.slice(0, 50)}...`);
  
  return storyBible;
}

/**
 * Clear cached Story Bible (call when book data changes)
 */
export function clearStoryBibleCache(bookId?: string) {
  if (bookId) {
    storyBibleCache.delete(bookId);
  } else {
    storyBibleCache.clear();
  }
}

/**
 * Analyze book content to extract characters and setting.
 * Uses CENTRALIZED completeText via edge function.
 */
async function analyzeBookContent(
  bookTitle: string,
  combinedText: string,
  illustrationDescriptions: string
): Promise<BookStoryBible> {
  if (!combinedText.trim()) {
    // Return minimal Story Bible
    return {
      title: bookTitle,
      characters: [],
      setting: 'A colorful children\'s book world',
      mood: 'whimsical and gentle',
      artStyle: TURN_STYLE_BASE,
    };
  }
  
  const systemPrompt = `You are analyzing a children's book to create a "Story Bible" for illustration consistency.
Extract the main characters, setting, and mood from the text.

IMPORTANT: Character descriptions should be VISUAL - focus on:
- What they look like (animal/human, size, colors)
- Distinctive features (clothing, accessories)
- Any consistent visual traits

Return JSON only:
{
  "characters": [
    { "name": "Character name or role", "description": "Visual description for illustration" }
  ],
  "setting": "The world/environment of the story",
  "mood": "One-word mood (e.g., whimsical, cozy, adventurous)"
}`;

  const userPrompt = `Book Title: "${bookTitle}"

Story Text:
${combinedText.slice(0, 2000)}

${illustrationDescriptions ? `\nIllustration Descriptions:\n${illustrationDescriptions.slice(0, 500)}` : ''}

Extract the Story Bible JSON.`;

  try {
    // Use CENTRALIZED completeText (routes through edge function)
    const result = await completeText(userPrompt, {
      systemPrompt,
      temperature: 0.3,
      maxTokens: 500,
      jsonResponse: true,
    });
    
    const parsed = (result.json || {}) as { 
      characters?: Array<{ name: string; description: string }>;
      setting?: string;
      mood?: string;
    };
    
    logger.debug(`[TurnIllustration] Story Bible created using ${result.model}`);
    
    return {
      title: bookTitle,
      characters: parsed.characters || [],
      setting: parsed.setting || 'A colorful children\'s book world',
      mood: parsed.mood || 'whimsical',
      artStyle: TURN_STYLE_BASE, // ALWAYS Turn aesthetic
    };
    
  } catch (error) {
    logger.warn('[TurnIllustration] Story Bible analysis failed:', error);
    return {
      title: bookTitle,
      characters: [],
      setting: 'A colorful children\'s book world',
      mood: 'whimsical and gentle',
      artStyle: TURN_STYLE_BASE,
    };
  }
}

// ============================================================================
// Scene Analysis
// ============================================================================

/**
 * Analyze page text to generate a scene description.
 * Uses CENTRALIZED completeText via edge function.
 */
export async function analyzePageForIllustration(
  pageText: string,
  bookTitle?: string,
  storyBible?: BookStoryBible
): Promise<SceneAnalysisResult> {
  // Build character context from Story Bible
  const characterContext = storyBible?.characters?.length 
    ? `\n\nKnown characters (use these descriptions for consistency):\n${storyBible.characters.map(c => `- ${c.name}: ${c.description}`).join('\n')}`
    : '';
  
  const settingContext = storyBible?.setting
    ? `\n\nBook setting: ${storyBible.setting}`
    : '';
  
  const systemPrompt = `You are an art director for children's book illustrations. 
Given page text, describe what the illustration should show.${characterContext}${settingContext}

IMPORTANT:
- Focus on VISUAL elements only
- If known characters appear, describe them using the established descriptions above
- Keep description under 100 words
- Make it child-friendly and whimsical

Return JSON:
{
  "sceneDescription": "Description of what to illustrate",
  "subjects": ["character1", "character2"],
  "mood": "one word mood"
}`;

  const userPrompt = bookTitle 
    ? `Book: "${bookTitle}"\n\nPage text:\n${pageText}`
    : `Page text:\n${pageText}`;

  try {
    // Use CENTRALIZED completeText (routes through edge function)
    const result = await completeText(userPrompt, {
      systemPrompt,
      temperature: 0.3,
      maxTokens: 300,
      jsonResponse: true,
    });
    
    const parsed = (result.json || {}) as {
      sceneDescription?: string;
      subjects?: string[];
      mood?: string;
    };
    
    return {
      sceneDescription: parsed.sceneDescription || pageText.slice(0, 200),
      sceneType: inferSceneType(pageText),
      subjects: parsed.subjects || [],
      mood: parsed.mood || 'gentle',
    };
  } catch (error) {
    logger.warn('[TurnIllustration] Scene analysis failed:', error);
    return {
      sceneDescription: pageText.slice(0, 200),
      sceneType: inferSceneType(pageText),
      subjects: [],
      mood: 'gentle',
    };
  }
}

/**
 * Analyze an original scanned image using GPT-4o Vision.
 * Uses CENTRALIZED completeTextWithVision via edge function.
 */
export async function analyzeImageForIllustration(
  imageBase64: string,
  pageText: string,
  bookTitle?: string,
  storyBible?: BookStoryBible
): Promise<SceneAnalysisResult> {
  // Build character context from Story Bible
  const characterContext = storyBible?.characters?.length 
    ? `\n\nKnown characters (identify these if present):\n${storyBible.characters.map(c => `- ${c.name}: ${c.description}`).join('\n')}`
    : '';
  
  const systemPrompt = `You are analyzing a scanned children's book page for illustration generation.
Describe the scene in detail for recreating it in a consistent art style.${characterContext}

Extract:
1. Characters/subjects (match to known characters if applicable)
2. Setting/environment
3. Action happening
4. Mood

Return JSON:
{
  "sceneDescription": "Detailed scene description (150-200 words)",
  "subjects": ["subject1", "subject2"],
  "mood": "one word"
}`;

  const userPrompt = pageText?.trim() 
    ? `Page text: "${pageText.slice(0, 500)}"${bookTitle ? `\nBook: "${bookTitle}"` : ''}\n\nAnalyze the image and describe the scene.`
    : `Analyze the image and describe the scene for illustration.${bookTitle ? ` Book: "${bookTitle}"` : ''}`;

  try {
    // Use CENTRALIZED completeTextWithVision (routes through edge function)
    const result = await completeTextWithVision(userPrompt, imageBase64, {
      systemPrompt,
      temperature: 0.3,
      maxTokens: 600,
      jsonResponse: true,
      imageDetail: 'high',
    });
    
    const parsed = (result.json || {}) as {
      sceneDescription?: string;
      subjects?: string[];
      mood?: string;
    };
    
    return {
      sceneDescription: parsed.sceneDescription || pageText?.slice(0, 200) || '',
      sceneType: inferSceneType(pageText || ''),
      subjects: parsed.subjects || [],
      mood: parsed.mood || 'gentle',
    };
  } catch (error) {
    // Fallback to text-only analysis if Vision fails
    logger.warn('[TurnIllustration] Image analysis failed, falling back to text:', error);
    return analyzePageForIllustration(pageText, bookTitle, storyBible);
  }
}

// ============================================================================
// Text Position Analysis
// ============================================================================

/**
 * Text position result for overlay placement
 */
export interface TextPositionResult {
  /** Where to place text overlay */
  position: 'top' | 'bottom' | 'center-top' | 'center-bottom';
  /** Confidence in the recommendation */
  confidence: number;
  /** Reason for the recommendation */
  reason: string;
}

/**
 * Analyze a generated Turn Art image to find the best zone for text overlay.
 * Uses CENTRALIZED completeTextWithVision via edge function.
 * 
 * @param imageUrl - Base64 data URL of the generated Turn Art
 * @returns Position recommendation for text overlay
 */
export async function analyzeImageForTextPlacement(
  imageUrl: string
): Promise<TextPositionResult> {
  const systemPrompt = `You are analyzing a children's book illustration to determine where to overlay text.
The text needs a "quiet" area with minimal visual detail so it remains readable with a frosted white background.

Analyze the image and determine which area has the LEAST visual complexity:
- "top" - upper 20-25% of the image
- "bottom" - lower 20-25% of the image  
- "center-top" - upper-center area
- "center-bottom" - lower-center area

Consider:
- Where is the main subject/action? (avoid placing text there)
- Where are simple colors, gradients, or sky/ground?
- Where would text be least distracting?

Return JSON:
{
  "position": "bottom",
  "confidence": 85,
  "reason": "Simple grass area at bottom, main character is centered/top"
}`;

  try {
    // Use CENTRALIZED completeTextWithVision (routes through edge function)
    const result = await completeTextWithVision(
      'Where should text be overlaid on this illustration?',
      imageUrl,
      {
        systemPrompt,
        temperature: 0.2,
        maxTokens: 200,
        jsonResponse: true,
        imageDetail: 'low',
      }
    );
    
    const parsed = (result.json || {}) as {
      position?: string;
      confidence?: number;
      reason?: string;
    };
    
    // Validate position
    const validPositions = ['top', 'bottom', 'center-top', 'center-bottom'];
    const position = validPositions.includes(parsed.position || '') 
      ? parsed.position 
      : 'bottom';
    
    return {
      position: position as TextPositionResult['position'],
      confidence: parsed.confidence || 70,
      reason: parsed.reason || 'AI analysis',
    };
  } catch (error) {
    logger.warn('[TurnIllustration] Text position analysis failed, using default:', error);
    return {
      position: 'bottom',
      confidence: 50,
      reason: 'Fallback due to analysis unavailability',
    };
  }
}

// ============================================================================
// Main Generation Function
// ============================================================================

/**
 * Generate a Turn-style illustration for a page.
 * 
 * Uses CENTRALIZED image generation (routes through edge function)
 * with fixed Turn aesthetic style.
 */
export async function generateTurnIllustration(
  request: TurnIllustrationRequest
): Promise<TurnIllustrationResult> {
  const { 
    pageText, 
    pageNumber, 
    originalImageBase64,
    sceneDescription, 
    sceneType, 
    bookTitle,
    storyBible,
  } = request;
  
  const startTime = Date.now();
  
  // Step 1: Analyze scene (with Story Bible context for consistency)
  let finalSceneDescription = sceneDescription;
  let finalSceneType = sceneType;
  
  if (!finalSceneDescription) {
    if (originalImageBase64) {
      const analysis = await analyzeImageForIllustration(
        originalImageBase64, 
        pageText, 
        bookTitle,
        storyBible
      );
      finalSceneDescription = analysis.sceneDescription;
      finalSceneType = finalSceneType || analysis.sceneType;
    } else {
      const analysis = await analyzePageForIllustration(pageText, bookTitle, storyBible);
      finalSceneDescription = analysis.sceneDescription;
      finalSceneType = finalSceneType || analysis.sceneType;
    }
  }
  
  // Step 2: Build additional context from Story Bible
  let additionalContext = '';
  if (bookTitle) {
    additionalContext += `From "${bookTitle}". `;
  }
  if (storyBible?.characters?.length) {
    additionalContext += `Characters: ${storyBible.characters.map(c => `${c.name} (${c.description})`).join('; ')}. `;
  }
  if (storyBible?.setting) {
    additionalContext += `Setting: ${storyBible.setting}. `;
  }
  
  // Step 3: Build Turn-style prompt (FIXED aesthetic)
  const prompt = buildTurnIllustrationPrompt(
    finalSceneDescription,
    finalSceneType || 'character_focus',
    additionalContext.trim() || undefined
  );
  
  logger.debug(`[TurnIllustration] Generating page ${pageNumber}...`);
  
  // Step 4: Generate using CENTRALIZED image service (routes through edge function)
  // Use 2:3 portrait aspect ratio for full-page/full-screen display
  const result = await generateImage(prompt, {
    temperature: 0.4,
    aspectRatio: '2:3', // Portrait for full-bleed phone screens
  });
  
  const generationTimeMs = Date.now() - startTime;
  logger.debug(`[TurnIllustration] Page ${pageNumber} generated in ${generationTimeMs}ms`);
  
  return {
    imageUrl: result.imageUrl,
    prompt,
    model: result.model,
    generationTimeMs,
  };
}

/**
 * Generate illustrations for multiple pages in batch.
 */
export async function generateTurnIllustrationsBatch(
  requests: TurnIllustrationRequest[],
  onProgress?: (completed: number, total: number, pageNumber: number) => void
): Promise<Array<TurnIllustrationResult | { error: string; pageNumber: number }>> {
  const results: Array<TurnIllustrationResult | { error: string; pageNumber: number }> = [];
  
  for (let i = 0; i < requests.length; i++) {
    const request = requests[i];
    
    try {
      const result = await generateTurnIllustration(request);
      results.push(result);
    } catch (error) {
      logger.error(`[TurnIllustration] Failed page ${request.pageNumber}:`, error);
      results.push({
        error: error instanceof Error ? error.message : 'Unknown error',
        pageNumber: request.pageNumber,
      });
    }
    
    onProgress?.(i + 1, requests.length, request.pageNumber);
    
    // Delay between requests
    if (i < requests.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
  
  return results;
}

// ============================================================================
// Exports
// ============================================================================

export { 
  buildTurnIllustrationPrompt, 
  inferSceneType,
  TURN_STYLE_BASE,
} from './styles';
export type { TurnSceneType } from './styles';

export default {
  generateTurnIllustration,
  generateTurnIllustrationsBatch,
  analyzePageForIllustration,
  analyzeImageForIllustration,
  analyzeImageForTextPlacement,
  getOrCreateStoryBible,
  clearStoryBibleCache,
};
